/**
 * SuperBonusC (divergence BL-04): the discount percentage an administrator may set for one
 * payment on the Payment page, as Delphi's administrator right-click panel does
 * (`ufrmAchitare.pas:1445-1495`). The backend prices it and accepts it from administrators only;
 * this module turns the typed text into the value sent and keeps the request in step with it.
 */

/** The largest percentage accepted, matching the backend's validation. */
export const MAX_CONTRACT_BONUS_PERCENT = 100;

/**
 * Read the typed percentage: `null` for an empty field (the usual discount applies), the whole
 * number for a valid entry, `undefined` for anything the backend would refuse.
 */
export const parseContractBonusPercent = (text: string): number | null | undefined => {
    const trimmed = text.trim();
    if (trimmed === '') {
        return null;
    }
    if (!/^\d+$/.test(trimmed)) {
        return undefined;
    }
    const value = Number(trimmed);
    return value <= MAX_CONTRACT_BONUS_PERCENT ? value : undefined;
};

/** The calculation endpoint for a contract, carrying the override when one is set. */
export const buildPaymentCalculationPath = (
    contractNr: number,
    contractBonusPercent: number | null,
): string =>
    contractBonusPercent === null
        ? `/payments/calculate/${contractNr}`
        : `/payments/calculate/${contractNr}?contractBonusPercent=${contractBonusPercent}`;

/**
 * Identity of the calculations on screen: the contracts and each one's override, so the page
 * re-prices a contract when its override changes and not when a row object is merely replaced.
 */
export const calculationRequestKey = (
    contracts: readonly { Nr: number }[],
    overrideOf: (contractNr: number) => number | null,
): string =>
    contracts.map(contract => `${contract.Nr}:${overrideOf(contract.Nr) ?? ''}`).join(',');
